import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Timer, RotateCcw } from "lucide-react";
import { http, errMsg } from "../../lib/api";
import { fmtSecs } from "../kit/SpeedPanel";

export const RetryButton = ({ kitId, reload, small, where = "kit" }) => {
  const [busy, setBusy] = useState(false);
  const go = async (e) => {
    e?.preventDefault(); e?.stopPropagation();
    setBusy(true);
    try { await http.post(`/kits/${kitId}/retry`); toast.success("Retrying — carrying on from where it stopped"); reload?.(); }
    catch (ex) { toast.error(errMsg(ex)); } finally { setBusy(false); }
  };
  return (
    <button data-testid={`retry-${where}-${kitId}`} onClick={go} disabled={busy}
      className={`inline-flex items-center gap-1.5 rounded-sm border border-red-300 text-red-700 hover:bg-red-50 transition-colors disabled:opacity-40 ${small ? "px-2 py-0.5 text-xs" : "px-4 py-2 text-sm font-semibold"}`}>
      <RotateCcw size={small ? 12 : 15} /> Retry
    </button>
  );
};

const Failed = ({ d, load }) => {
  const retryAll = async () => {
    try { const r = await http.post("/kits-retry-failed"); toast.success(`${r.data.queued} kit(s) lined up — they retry two at a time`); load(); }
    catch (e) { toast.error(errMsg(e)); }
  };
  if (!d.failed.length) return <p data-testid="speed-no-failed" className="text-xs text-stone-500 mt-4">No stopped kits.</p>;
  return (
    <div className="mt-5">
      <div className="flex items-center gap-3 mb-2">
        <b data-testid="speed-failed-count" className="text-sm text-red-700">{d.failed.length} stopped kit(s)</b>
        <button data-testid="retry-all-failed" onClick={retryAll} className="inline-flex items-center gap-1.5 rounded-sm bg-[#2B2A26] px-3 py-1.5 text-xs font-semibold text-white hover:bg-black"><RotateCcw size={12} /> Retry all failed</button>
      </div>
      <ul className="text-xs divide-y divide-stone-100 border border-stone-100">
        {d.failed.map((k) => (
          <li key={k.id} data-testid={`speed-failed-${k.id}`} className="flex items-center gap-3 px-3 py-1.5">
            <Link to={`/kits/${k.id}`} className="font-semibold text-[#2B2A26] hover:underline w-44 truncate">{k.destination}</Link>
            <span className="text-stone-500 truncate flex-1" title={k.error}>{k.error}</span>
            <span data-testid={`speed-auto-${k.id}`} className="text-stone-500 shrink-0">{(k.auto_retries || 0) >= d.auto_tries ? `auto-retried ${d.auto_tries}× — needs you` : `auto-retry ${(k.auto_retries || 0) + 1} of ${d.auto_tries} soon`}</span>
            {k.retry_queued ? <span className="text-sky-700">Lined up</span> : <RetryButton kitId={k.id} reload={load} small where="speed" />}
          </li>
        ))}
      </ul>
    </div>
  );
};

const SlowList = ({ rows, testid, title }) => !rows.length ? null : (
  <div className="mt-4">
    <div className="text-xs font-semibold text-stone-600 mb-1">{title}</div>
    <ul data-testid={testid} className="text-xs divide-y divide-stone-100 border border-stone-100">
      {rows.map((s, i) => (
        <li key={i} className={`flex items-center gap-3 px-3 py-1.5 ${s.secs >= 600 ? "bg-red-50" : ""}`}>
          <Link to={`/kits/${s.kit_id}`} className="font-semibold text-[#2B2A26] hover:underline w-44 truncate">{s.destination}</Link>
          <span className="w-48">{s.phase}</span><span className="text-stone-500 truncate flex-1">{s.detail}</span>
          <span className={`font-mono-ui ${s.secs >= 600 ? "text-red-700 font-bold" : ""}`}>{fmtSecs(s.secs)}</span>
        </li>
      ))}
    </ul>
  </div>
);

export const SpeedCard = () => {
  const [d, setD] = useState(null);
  const load = () => http.get("/speed").then((r) => setD(r.data)).catch(() => {});
  useEffect(() => { load(); const t = setInterval(load, 30000); return () => clearInterval(t); }, []); // eslint-disable-line react-hooks/exhaustive-deps
  if (!d) return null;
  return (
    <div data-testid="speed-card" className="border border-stone-200 bg-white p-5 mb-6">
      <div className="flex items-center gap-2 mb-3"><Timer size={16} className="text-stone-500" /><b className="text-[#2B2A26]">Kit build speed</b><span className="text-xs text-stone-500">last 30 days · steps over 10 min are slow</span></div>
      {d.phases.length ? (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-2">
          {d.phases.map((p) => (
            <div key={p.phase} data-testid={`speed-phase-${p.phase}`} className={`border px-3 py-2 ${p.avg >= 600 ? "border-red-300 bg-red-50" : "border-stone-200"}`}>
              <div className="text-xs font-semibold text-[#2B2A26] truncate">{p.phase}</div>
              <div className={`font-mono-ui text-sm ${p.avg >= 600 ? "text-red-700 font-bold" : ""}`}>avg {fmtSecs(p.avg)}</div>
              <div className="text-[11px] text-stone-500">longest {fmtSecs(p.max)} · {p.slow}/{p.count} slow</div>
            </div>
          ))}
        </div>
      ) : <p className="text-xs text-stone-500">Timings appear here as kits build.</p>}
      <SlowList rows={d.running} testid="speed-running" title="Running now" />
      <SlowList rows={d.slow} testid="speed-slowest" title="Slowest steps" />
      <Failed d={d} load={load} />
    </div>
  );
};
